import React from "react";
import { Truck, Sparkles, Check, ArrowUpRight } from "lucide-react";
import { useLang } from "../lib/LangContext";
import { Reveal } from "./Reveal";
import { TID } from "../lib/testIds";

const TRANSPORT_IMG =
  "https://images.pexels.com/photos/11446697/pexels-photo-11446697.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const CLEANING_IMG =
  "https://images.unsplash.com/photo-1627905646269-7f034dcc5738?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxwcmVtaXVtJTIwb2ZmaWNlJTIwY2xlYW5pbmclMjBzZXJ2aWNlc3xlbnwwfHx8fDE3ODA1NjkwMDJ8MA&ixlib=rb-4.1.0&q=85";

function ServiceCard({ icon: Icon, image, title, description, items, testId, accent = false }) {
  const scrollToQuote = () => {
    const el = document.getElementById("quote");
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 72;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };
  return (
    <div
      data-testid={testId}
      className="group relative bg-[#151515] border border-white/10 hover:border-[#D4AF37]/50 transition-all duration-500 overflow-hidden"
    >
      <div className="relative h-56 sm:h-64 overflow-hidden">
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#151515] via-[#151515]/60 to-transparent" />
        <div className="absolute top-4 left-4">
          <div className={`p-3 ${accent ? "bg-[#E3000F]" : "bg-[#D4AF37]"} text-black`}>
            <Icon className="w-5 h-5" strokeWidth={2} />
          </div>
        </div>
      </div>

      <div className="p-8 sm:p-10">
        <h3 className="font-display text-3xl sm:text-4xl text-white tracking-tight">{title}</h3>
        <p className="mt-3 text-white/60 text-sm sm:text-base leading-relaxed">{description}</p>

        <ul className="mt-6 space-y-3">
          {items.map((it, i) => (
            <li key={i} className="flex items-start gap-3 text-white/85 text-sm sm:text-base">
              <Check className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" strokeWidth={2.5} />
              <span>{it}</span>
            </li>
          ))}
        </ul>

        <button
          type="button"
          onClick={scrollToQuote}
          className="mt-8 inline-flex items-center gap-2 text-[#D4AF37] hover:text-white transition-colors text-sm uppercase tracking-[0.2em] font-semibold gold-link"
          data-testid={`${testId}-cta`}
        >
          <span>Offerte</span>
          <ArrowUpRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default function Services() {
  const { t } = useLang();
  return (
    <section
      id="services"
      data-testid={TID.servicesSection}
      className="relative bg-[#0A0A0A] py-24 md:py-32 grain"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="max-w-3xl">
            <span className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">
              — {t.services.eyebrow}
            </span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight mt-4 leading-tight">
              {t.services.title}
            </h2>
          </div>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          <Reveal delay={100}>
            <ServiceCard
              icon={Truck}
              image={TRANSPORT_IMG}
              title={t.services.transport.title}
              description={t.services.transport.description}
              items={t.services.transport.items}
              testId={TID.transportCard}
            />
          </Reveal>
          <Reveal delay={200}>
            <ServiceCard
              icon={Sparkles}
              image={CLEANING_IMG}
              title={t.services.cleaning.title}
              description={t.services.cleaning.description}
              items={t.services.cleaning.items}
              testId={TID.cleaningCard}
              accent
            />
          </Reveal>
        </div>
      </div>
    </section>
  );
}
